import { useEffect, useMemo } from 'react';
import { CINCELClient } from '@cincel/client';
import { useToast } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { translations } from 'translations';
import { ERROR_TOAST_DEFAULTS, logger } from '@helpers';
import { AxiosError } from 'axios';
import { useSession } from 'store/session';

import { resolveTokenV3, getStorage } from 'helpers/sessionContext';

export function useClient({ url = '', token = '', useAuth = true } = {}) {
  const { t } = useTranslation();
  const toast = useToast();
  const { setIsV3Expired } = useSession();

  const finalUrl = useMemo(() => url || (process.env.REACT_APP_API_V3 as string), [url]);
  const newToken = useMemo(
    () => token || (useAuth ? resolveTokenV3() : ''),
    [token, useAuth]
  );

  const client = useMemo(
    () =>
      new CINCELClient({
        baseURL: finalUrl,
        token: newToken as string,
      }),
    [newToken, finalUrl],
  );
  const axios = client.getAxiosInstance();

  useEffect(() => {
    // Simulate slow network (Industry standard for testing loaders)
    const reqInterceptor = axios.interceptors.request.use(async (config) => {
      if (getStorage().getItem('CINCEL_SIMULATE_SLOW_NET') === 'true') {
        await new Promise((resolve) => setTimeout(resolve, 2000));
      }
      return config;
    });

    const resInterceptor = axios.interceptors.response.use(
      (response) => {
        return response;
      },
      (error: AxiosError | any) => {
        // 1. Log all errors to console
        logger.error('AX Error', error);

        const isNetworkError = error?.message?.includes('Network Error');

        if (isNetworkError) {
          // 2. Prevent duplicate network error toasts
          const id = 'network-error-toast';
          if (!toast.isActive(id)) {
            toast({
              id,
              ...ERROR_TOAST_DEFAULTS,
              title: t(translations.ERRORS.NETWORK_ERROR),
            });
          }
        }

        // Logic to detect V3 expiration (Unauthorized)
        // Skip for unauthenticated clients (likely login attempts where 401 is expected for wrong credentials)
        if (
          useAuth &&
          error?.response?.status === 401 &&
          finalUrl.includes(process.env.REACT_APP_API_V3 as string)
        ) {
          setIsV3Expired(true);
        }

        return Promise.reject(error);
      },
    );

    // Cleanup: Eject interceptors to prevent memory leaks and duplicate execution 
    // when dependencies change or component unmounts
    return () => {
      axios.interceptors.request.eject(reqInterceptor);
      axios.interceptors.response.eject(resInterceptor);
    };
  }, [axios, t, toast, finalUrl, setIsV3Expired, useAuth]);

  return client;
}
